import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { API } from "@/api";
import { setTokens, clearTokens, loadTokens } from "@/lib/tokenStore";
import { clearAll as clearPageCache } from "@/lib/pageCache";
import { isDemoLoopEmail, runDemoLoopReset } from "@/lib/demoLoop";

const AuthContext = createContext(null);

// Best-effort error-body parser used by the fetch fallback in
// consumeMagicLink. Backend returns `{detail: "..."}` as JSON; sometimes
// proxies return plain text. We try JSON first, then return the raw text
// (capped to 200 chars so the UI never renders a wall of HTML on a 500).
function tryDetail(txt) {
    try { return JSON.parse(txt).detail; }
    catch { return (txt || "").slice(0, 200); }
}

// After any successful auth event, demo-loop accounts replay onboarding +
// the spotlight tour. We reset the server-side flags and refresh the local
// user object so ProtectedRoute immediately bounces them into HealthSources.
async function applyDemoLoop(user, setUser) {
    if (!isDemoLoopEmail(user?.email)) return user;
    const fresh = await runDemoLoopReset();
    if (fresh) {
        setUser(fresh);
        return fresh;
    }
    return user;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=loading, false=anon, object=user
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        // CRITICAL: if returning from OAuth callback, skip /auth/me here.
        // AuthCallback will exchange the session_id and then trigger refresh.
        if (typeof window !== "undefined" &&
            window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(false);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Hydrate token cache BEFORE the first /auth/me — otherwise the
        // request fires without a bearer header on cold-mount and we
        // bounce to login even though the user has a valid refresh token.
        (async () => {
            await loadTokens();
            refresh();
        })();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        await setTokens({
            access:  data.access_token  || null,
            refresh: data.refresh_token || null,
        });
        setUser(data.user);
        // Surface daily-login XP if the backend awarded any.
        if (data.xp_awarded && data.xp_awarded > 0) {
            try {
                const { toast } = await import("sonner");
                toast.success(`+${data.xp_awarded} XP · welkom terug`, { duration: 2400 });
            } catch {}
            try { window.dispatchEvent(new Event("wyng:xp-event")); } catch {}
        }
        return await applyDemoLoop(data.user, setUser);
    };

    const register = async (email, password, name, gender,
                             { first_name = "", last_name = "", accepted_terms = false, dob = null } = {}) => {
        const { data } = await api.post("/auth/register", {
            email, password, name, gender,
            first_name, last_name, accepted_terms, dob,
        });
        await setTokens({
            access:  data.access_token  || null,
            refresh: data.refresh_token || null,
        });
        setUser(data.user);
        return await applyDemoLoop(data.user, setUser);
    };

    const exchangeGoogleSession = async (sessionId) => {
        const { data } = await api.post("/auth/google/exchange", { session_id: sessionId });
        await setTokens({
            access:  data.access_token  || null,
            refresh: data.refresh_token || null,
        });
        setUser(data.user);
        return await applyDemoLoop(data.user, setUser);
    };

    /** Magic-link consume — pass the URL token, receive JWT, sign in.
     * Optionally attaches a referral code (from `?ref=` capture) so the
     * backend can wire up a pending referral on first-time signup. */
    const consumeMagicLink = async (token) => {
        let refCode = "";
        try { refCode = sessionStorage.getItem("wyng_ref_code") || ""; } catch {}
        const body = { token };
        if (refCode) body.ref_code = refCode;
        const bodyStr = JSON.stringify(body);

        // iOS-Capacitor magic-link cold-launch flow (Jun 2026):
        //
        // We try TWO content-types in sequence:
        //
        //   1) application/json — works on production (current backend),
        //      but triggers CORS preflight OPTIONS which can be slow on
        //      a cold-launched WKWebView behind Cloudflare bot mgmt.
        //
        //   2) text/plain — CORS "simple request" → no preflight. Faster
        //      on iOS cold-launch. Backend was updated to accept this
        //      content-type but the change is preview-only until the
        //      next production deploy.
        //
        // JSON-first means we ALWAYS work against the current production
        // backend. Once the user deploys, text/plain becomes the faster
        // primary via the fallback swap below.
        async function tryPost(contentType) {
            const ctl = new AbortController();
            const killer = setTimeout(() => ctl.abort(), 10000);
            try {
                const r = await fetch(`${API}/auth/magic/consume`, {
                    method:  "POST",
                    signal:  ctl.signal,
                    headers: { "Content-Type": contentType },
                    body:    bodyStr,
                });
                if (r.status === 422) {
                    // Backend rejected this content-type — try the other.
                    return { retry: true };
                }
                if (!r.ok) {
                    const txt = await r.text().catch(() => "");
                    const err = new Error(tryDetail(txt) || `HTTP ${r.status}`);
                    err.response = { status: r.status,
                                     data: { detail: tryDetail(txt) } };
                    return { error: err };
                }
                return { data: await r.json() };
            } catch (e) {
                return { error: e };
            } finally {
                clearTimeout(killer);
            }
        }

        // Primary: JSON (always supported by prod). Fallback: text/plain.
        let result = await tryPost("application/json");
        if (result.retry) result = await tryPost("text/plain");
        if (result.error) throw result.error;
        const data = result.data;

        try { sessionStorage.removeItem("wyng_ref_code"); } catch {}
        await setTokens({
            access:  data.access_token  || null,
            refresh: data.refresh_token || null,
        });
        setUser(data.user);
        return await applyDemoLoop(data.user, setUser);
    };

    /** Tester-only demo bypass — signs in as the configured tester. */
    const loginAsDemo = async () => {
        const { data } = await api.post("/auth/demo");
        await setTokens({
            access:  data.access_token  || null,
            refresh: data.refresh_token || null,
        });
        setUser(data.user);
        return data.user;
    };

    /** Email OTP — request a 6-digit login/reset code by mail. */
    const requestEmailCode = async (email, purpose = "login") => {
        const { data } = await api.post("/auth/email/request-code",
            { email: email.trim().toLowerCase(), purpose });
        return data;  // {ok, expires_in_seconds}
    };

    /** Email OTP — verify code. For purpose=login returns JWT and signs the
     * user in. For purpose=reset requires `newPassword` and returns {ok}. */
    const verifyEmailCode = async (email, code, { purpose = "login", newPassword = null } = {}) => {
        const body = { email: email.trim().toLowerCase(), code: code.trim(), purpose };
        if (purpose === "reset") body.new_password = newPassword;
        const { data } = await api.post("/auth/email/verify-code", body);
        if (purpose === "login" && data.access_token) {
            await setTokens({
                access:  data.access_token  || null,
                refresh: data.refresh_token || null,
            });
            setUser(data.user);
            await applyDemoLoop(data.user, setUser);
        }
        return data;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            // ignore network/auth errors during logout
        }
        await clearTokens();
        clearPageCache();           // wipe per-tab caches so the next login starts fresh
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh, exchangeGoogleSession, requestEmailCode, verifyEmailCode, consumeMagicLink, loginAsDemo }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiError(detail) {
    if (detail == null) return "Something went wrong";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
